import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { ButtonGroup } from '@mui/material';
import { s } from '@/theme/tokens';
import { Box, Text } from '@/ui';
import { CopyIcon, DeleteIcon, RotateRight, Tooltip } from '@/ui/primitives';
import { Button } from '@/ui/primitives/Button';
import { TextField } from '@/ui/primitives/TextField';

export type PipelineWebhookActionStyles = {
  actionGroupSx: Record<string, unknown>;
  actionButtonSx: Record<string, unknown>;
};

export const PipelineWebhookDescriptionSection = (): JSX.Element => (
  <Text role="body" color="textSecondary">
    Use the Webhook URL below and generate a Webhook Key. Add both to CI/CD provider&apos;s webhook
    URL and secret token fields.
  </Text>
);

type PipelineWebhookUrlSectionProps = PipelineWebhookActionStyles & {
  webhookEndpoint: string;
  onCopy: () => void;
};

export const PipelineWebhookUrlSection = ({
  webhookEndpoint,
  actionGroupSx,
  actionButtonSx,
  onCopy,
}: PipelineWebhookUrlSectionProps): JSX.Element => (
  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: s('md') }}>
    <Box sx={{ flex: 1 }}>
      <TextField
        label="Webhook URL"
        value={webhookEndpoint}
        slotProps={{ input: { readOnly: true } }}
        onFocus={(event) => {
          event.target.select();
        }}
      />
    </Box>
    <ButtonGroup variant="outlined" aria-label="Webhook URL actions" sx={actionGroupSx}>
      <Button
        variant="ghost"
        fullWidthOnMobile={false}
        disableRipple
        aria-label="Copy URL"
        sx={actionButtonSx}
        onClick={onCopy}
      >
        <Tooltip title="Copy Webhook URL" placement="top">
          <CopyIcon />
        </Tooltip>
      </Button>
    </ButtonGroup>
  </Box>
);

type PipelineWebhookKeySectionProps = PipelineWebhookActionStyles & {
  value: string;
  canCopy: boolean;
  canTest: boolean;
  canGenerateOrRegenerate: boolean;
  canRevoke: boolean;
  generateLabel: string;
  onCopy: () => void;
  onTest: () => void;
  onGenerateOrRegenerate: () => void;
  onRevoke: () => void;
};

export const PipelineWebhookKeySection = ({
  value,
  canCopy,
  canTest,
  canGenerateOrRegenerate,
  canRevoke,
  generateLabel,
  actionGroupSx,
  actionButtonSx,
  onCopy,
  onTest,
  onGenerateOrRegenerate,
  onRevoke,
}: PipelineWebhookKeySectionProps): JSX.Element => (
  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: s('md') }}>
    <Box sx={{ flex: 1 }}>
      <TextField
        label="Webhook Key"
        value={value}
        placeholder="Generate a new key to enable pipeline webhooks"
        slotProps={{ input: { readOnly: true } }}
        sx={{ '& .MuiOutlinedInput-input': { color: 'text.secondary' } }}
        onFocus={(event) => {
          event.target.select();
        }}
      />
    </Box>
    <ButtonGroup variant="outlined" aria-label="Webhook key actions" sx={actionGroupSx}>
      <Button
        variant="ghost"
        fullWidthOnMobile={false}
        disableRipple
        aria-label={generateLabel}
        disabled={!canGenerateOrRegenerate}
        sx={actionButtonSx}
        onClick={onGenerateOrRegenerate}
      >
        <Tooltip title={generateLabel} placement="top">
          <RotateRight sx={{ fontSize: 21 }} />
        </Tooltip>
      </Button>
      <Button
        variant="ghost"
        fullWidthOnMobile={false}
        disableRipple
        aria-label="Test Webhook Key"
        disabled={!canTest}
        sx={[actionButtonSx, { minWidth: 56, width: 56, maxWidth: 56 }]}
        onClick={onTest}
      >
        <Tooltip title="Test Webhook Key" placement="top">
          <PlayArrowIcon sx={{ fontSize: 22 }} />
        </Tooltip>
      </Button>
      <Button
        variant="ghost"
        fullWidthOnMobile={false}
        disableRipple
        aria-label="Copy Key"
        disabled={!canCopy}
        sx={actionButtonSx}
        onClick={onCopy}
      >
        <Tooltip title="Copy Webhook Key" placement="top">
          <CopyIcon />
        </Tooltip>
      </Button>
      <Button
        variant="ghost"
        fullWidthOnMobile={false}
        disableRipple
        aria-label="Revoke Key"
        disabled={!canRevoke}
        sx={actionButtonSx}
        onClick={onRevoke}
      >
        <Tooltip title="Revoke Webhook Key" placement="top">
          <DeleteIcon sx={{ fontSize: 20 }} />
        </Tooltip>
      </Button>
    </ButtonGroup>
  </Box>
);
